import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { X, Send, AlertTriangle, AlertCircle, RotateCw, MessageCircle, Check, XCircle, Pill, Heart, Stethoscope, Users, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, MapPin, Shield, Mic, MicOff, FileText, Edit3, Activity, ArrowDown, Keyboard } from 'lucide-react'
import useAgentStore from '../../stores/useAgentStore'
import { useAgent } from '../AgentContext'
import { foldThread, prepMsgRef } from '../../stores/agentPrepRef'
import { useProfileStore, useCaseStore } from '../../stores'
// ★ TEST 1.03 — a THIRD writer of `conditions`. `[...existing, x]` where `existing` is the
//   object the Auto-fill import wrote throws "is not iterable": the agent could not add a
//   condition to a poisoned profile at all.
import { toConditionList } from '../../utils/profileConditions'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import orellaOWhite from '../../assets/logos/orella_o_white.png'
import Sources from '../Sources'
import { useI18n } from '../../i18n'
import useVoiceInput from '../../hooks/useVoiceInput'
import { DIALOGUE_BUBBLE, DIALOGUE_TEXT, DIALOGUE_PAD } from './dialogueBubble'
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

// Screen route map for navigation actions
const SCREEN_ROUTES = {
  home: '/home',
  case_home: '/case/home',
  describe: '/case/describe/voice', // First describe step is voice
  describe_voice: '/case/describe/voice',
  describe_details: '/case/describe/details',
  describe_review: '/case/describe/review',
  detect: '/case/detect',
  diagnose: '/case/diagnose',
  deliver: '/case/deliver',
  share: '/case/share',
  share_choice: '/case/share-hub',   // s98 D468: the hub replaced the fork
  profile: '/profile',
  profile_allergies: '/profile/allergies',
  profile_medications: '/profile/medications',
  profile_medical_history: '/profile/medical-history',
  cases: '/cases',
  visits: '/visits',
  settings: '/settings',
  // Specialty routes — agent can deep-link to start a case for a specific specialty
  specialty_ear: '/specialty/ear',
  specialty_nose: '/specialty/nose',
  specialty_throat: '/specialty/throat',
  specialty_eye: '/specialty/eye',
  specialty_skin: '/specialty/skin',
  specialty_stomach: '/specialty/stomach-gut',
  specialty_urinary: '/specialty/urinary',
  specialty_pain: '/specialty/pain',
  specialty_muscle: '/specialty/muscle-joints',
  specialty_head_neck: '/specialty/head-neck',
  specialty_wellness: '/specialty/health-wellness',
  specialty_general: '/specialty/general',
  // v226.9 — destination expansion so the agent can route everywhere it describes
  case_authorizations: '/case/authorizations',
  schedule_followup: '/case/schedule-followup',
  case_agreement: '/case/agreement',
  case_note: '/case/note',
  profile_insurance: '/profile/insurance',
  profile_identification: '/profile/identification',
  profile_family_history: '/profile/family-history',
  profile_social: '/profile/social-lifestyle',
  profile_import_records: '/profile/import-records',
  doctors: '/doctors',
  notifications: '/notifications',
  notification_preferences: '/notification-preferences',
  settings_security: '/settings/security',
  settings_two_factor: '/settings/two-factor',
  settings_change_password: '/settings/change-password',
  settings_privacy: '/settings/privacy-policy',
  settings_terms: '/settings/terms',
  describe_signs_symptoms: '/case/describe/signs-symptoms',
  describe_context: '/case/describe/context',
  describe_additional: '/case/describe/additional',
  describe_summary: '/case/describe/summary',
  deliver_instructions: '/case/deliver/instructions',
  deliver_medication_survey: '/case/deliver/medication-survey',
  deliver_further_testing: '/case/deliver/further-testing',
}

// Default labels for screens
const SCREEN_LABEL_KEYS = {
  home: 'screenHome',
  case_home: 'screenCaseHome',
  describe: 'screenStartDescribing',
  describe_voice: 'screenVoiceRecording',
  describe_details: 'screenSymptomDetails',
  describe_review: 'screenReviewDescription',
  detect: 'screenDetect',
  diagnose: 'screenDiagnose',
  deliver: 'screenDeliver',
  share: 'screenShareCase',
  share_choice: 'screenChooseYourPath',
  profile: 'screenProfile',
  profile_allergies: 'screenProfileAllergies',
  profile_medications: 'screenProfileMedications',
  profile_medical_history: 'screenProfileMedicalHistory',
  cases: 'screenCases',
  visits: 'screenVisits',
  settings: 'screenSettings',
  specialty_ear: 'screenStartEarCase',
  specialty_nose: 'screenStartNoseCase',
  specialty_throat: 'screenStartThroatCase',
  specialty_eye: 'screenStartEyeCase',
  specialty_skin: 'screenStartSkinCase',
  specialty_stomach: 'screenStartStomachCase',
  specialty_urinary: 'screenStartUrinaryCase',
  specialty_pain: 'screenStartPainCase',
  specialty_muscle: 'screenStartMuscleCase',
  specialty_head_neck: 'screenStartNeckCase',
  specialty_wellness: 'screenStartWellnessCase',
}

/** Navigation action card — deep links to an app screen */
function NavigationCard({ navigation, onNavigate }) {
  const { t } = useI18n()
  if (!navigation?.screen) return null

  // v206.3: case_continue is a special screen — it sets currentCase then navigates
  if (navigation.screen === 'case_continue' && navigation.caseId) {
    // s120 HOME-CASES-1: the Home chat opens the case the patient means; the label is the app's own (the server's
    //   is English), "Open case" / "Abrir el caso".
    const label = t('agent.openCase', 'Open case')
    return (
      <div className="ml-9 my-1.5 animate-[agentFadeUp_0.3s_ease]">
        <button
          onClick={() => onNavigate({ action: 'continue_case', caseId: navigation.caseId })}
          className="w-full flex items-center gap-2.5 px-3.5 py-2.5 bg-[#15354F] rounded-xl active:scale-[0.97] transition-transform shadow-sm"
        >
          <div className="w-6 h-6 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <MapPin size={13} className="text-white" />
          </div>
          <span className="text-[13px] font-semibold text-white flex-1 text-left">{label}</span>
          <ChevronRight size={15} className="text-white/60" />
        </button>
      </div>
    )
  }

  const route = SCREEN_ROUTES[navigation.screen]
  if (!route) return null
  const labelKey = SCREEN_LABEL_KEYS[navigation.screen]
  const label = navigation.label || (labelKey ? t('agent.' + labelKey) : t('agent.go'))

  return (
    <div className="ml-9 my-1.5 animate-[agentFadeUp_0.3s_ease]">
      <button
        onClick={() => onNavigate(route)}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 bg-[#15354F] rounded-xl active:scale-[0.97] transition-transform shadow-sm"
      >
        <div className="w-6 h-6 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0">
          <MapPin size={13} className="text-white" />
        </div>
        <span className="text-[13px] font-semibold text-white flex-1 text-left">{label}</span>
        <ChevronRight size={15} className="text-white/60" />
      </button>
    </div>
  )
}

// TypingDots
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 h-5">
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[agentDotPulse_1.2s_ease-in-out_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[agentDotPulse_1.2s_ease-in-out_infinite_0.2s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[agentDotPulse_1.2s_ease-in-out_infinite_0.4s]" />
    </span>
  )
}

// A stable empty identity: `|| []` builds a NEW array every render, which
// defeats the selector's reference check and re-renders on every store tick.
const EMPTY_ACTIONS = []

// Action icons by type
const ACTION_CONFIG = {
  add_allergy: { icon: AlertTriangle, labelKey: 'addAllergy', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  add_medication: { icon: Pill, labelKey: 'addMedication', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  add_condition: { icon: Heart, labelKey: 'addCondition', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' },
  add_surgery: { icon: Stethoscope, labelKey: 'addSurgery', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  add_family_condition: { icon: Users, labelKey: 'addFamilyCondition', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
  set_social: { icon: Activity, labelText: 'Social & lifestyle', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
}

function formatActionSummary(action) {
  const d = action.data || {}
  switch (action.action) {
    case 'add_allergy':
      return `${d.name}${d.severity ? ` (${d.severity})` : ''}${d.reaction ? ` — ${d.reaction}` : ''}`
    case 'add_medication':
      return `${d.name}${d.dosage ? ` ${d.dosage}` : ''}${d.frequency ? `, ${d.frequency}` : ''}`
    case 'add_condition':
      return `${d.name}${d.status ? ` (${d.status})` : ''}${d.yearDiagnosed ? `, since ${d.yearDiagnosed}` : ''}`
    case 'add_surgery':
      return `${d.name}${d.year ? ` (${d.year})` : ''}`
    case 'add_family_condition':
      return `${d.conditionName}${d.relatives?.length ? ` — ${d.relatives.join(', ')}` : ''}`
    case 'set_social': {
      const parts = []
      if (d.smoking) parts.push(`Tobacco: ${d.smoking}${d.smokingFrequency ? ` (${d.smokingFrequency})` : ''}`)
      if (d.alcohol) parts.push(`Alcohol: ${d.alcohol}${d.alcoholFrequency ? ` (${d.alcoholFrequency})` : ''}`)
      if (d.cannabis) parts.push(`Cannabis: ${d.cannabis}${d.cannabisFrequency ? ` (${d.cannabisFrequency})` : ''}`)
      if (d.exercise) parts.push(`Exercise: ${d.exercise}`)
      if (d.occupation) parts.push(`Occupation: ${d.occupation}`)
      return parts.join(' · ') || 'Social & lifestyle update'
    }
    default:
      return d.name || JSON.stringify(d)
  }
}

/** Inline profile action card shown in the chat */
function ProfileActionCard({ action, onAccept, onDismiss }) {
  const { t } = useI18n()
  const config = ACTION_CONFIG[action.action] || ACTION_CONFIG.add_condition
  const Icon = config.icon

  if (action.status === 'accepted') {
    return (
      <div className="ml-9 my-1.5 animate-[agentFadeUp_0.3s_ease]">
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
          <Check size={14} className="text-green-600 flex-shrink-0" />
          <span className="text-[12px] text-green-700 font-medium">{t('agent.addedToYourProfile')}</span>
        </div>
      </div>
    )
  }

  if (action.status === 'dismissed') {
    return null
  }

  return (
    <div className="ml-9 my-1.5 animate-[agentFadeUp_0.3s_ease]">
      <div className={`${config.bg} border ${config.border} rounded-xl px-3 py-2.5`}>
        <div className="flex items-start gap-2">
          <Icon size={15} className={`${config.color} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{config.labelText || t('agent.' + config.labelKey)}</p>
            <p className="text-[13px] font-medium text-[#15354F] mt-0.5">{formatActionSummary(action)}</p>
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => onAccept(action.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-[#15354F] text-white rounded-lg text-[12px] font-semibold active:scale-[0.97] transition-transform"
          >
            <Check size={13} /> {t('agent.addToProfile', 'Add to profile')}
          </button>
          <button
            onClick={() => onDismiss(action.id)}
            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-500 rounded-lg text-[12px] font-medium active:scale-[0.97] transition-transform"
          >
            {t('common.skip')}
          </button>
        </div>
      </div>
    </div>
  )
}

// v132.6.4 / v170.1.0: Word-by-word streaming reveal for assistant messages
// Speed calibrated to ~1.5x average reading pace (350-400 WPM)
// Average reading = 200-250 WPM (~250ms/word). We target ~150ms base + punctuation pauses.
function StreamingText({ text, onComplete }) {
  const { t } = useI18n()
  const [visibleCount, setVisibleCount] = useState(0)
  const words = useRef(text.split(/(\s+)/)) // Split keeping whitespace
  const totalParts = words.current.length

  useEffect(() => {
    if (visibleCount >= totalParts) {
      onComplete?.()
      return
    }
    const currentPart = words.current[visibleCount] || ''
    const trimmed = currentPart.trim()
    const isWhitespace = !trimmed
    const isPeriodOrQuestion = /[.!?]$/.test(trimmed)
    const isCommaOrSemicolon = /[,;:]$/.test(trimmed)
    const isNewline = /\n/.test(currentPart)
    const isShortWord = trimmed.length <= 3

    // Whitespace tokens reveal instantly
    if (isWhitespace) {
      const timer = setTimeout(() => setVisibleCount(v => v + 1), 5)
      return () => clearTimeout(timer)
    }

    // Base: ~150ms per word (~400 WPM, slightly ahead of reading pace)
    // Short common words (a, the, is, in) go faster — reader's eye skips them
    // Punctuation gets natural pauses — period/question = breath, comma = beat
    // Newlines get a longer pause — paragraph break
    let delay = isShortWord ? 90 : 140
    if (isCommaOrSemicolon) delay = 220
    if (isPeriodOrQuestion) delay = 350
    if (isNewline) delay = 400

    const timer = setTimeout(() => setVisibleCount(v => v + 1), delay)
    return () => clearTimeout(timer)
  }, [visibleCount, totalParts])

  return (
    <p className={DIALOGUE_TEXT}>
      {words.current.slice(0, visibleCount).join('')}
      {visibleCount < totalParts && <span className="inline-block w-[3px] h-[16px] bg-[#46A8F9] ml-[1px] rounded-sm animate-pulse align-text-bottom" />}
    </p>
  )
}

// Message bubble
// v220.1 — Patient-confirmation card for an agent-prepared escalation to the MD.
// Renders inline below the agent bubble. Patient must tap Send before anything
// v234.4 — Shared reason-aware failure copy for the chat agent (free-text sends
// and the send-to-doctor card). Mirrors the store's status classifier.
// ★ PROD-1157: 'client' = the app refused before any request left it (the store says kind
//   'client', or gives no status at all). Nothing reached a server, so never "on our end".
function classifyAgentStatus(status, clientKind) {
  if (clientKind === 'client' || status === undefined) return 'client'
  if (status === 429) return 'rate'
  if (status === 0 || status === 408) return 'offline'
  return 'server'
}
function agentErrorCopy(kind, language, t) {
  const es = language === 'es'
  switch (kind) {
    case 'client':
      // An approved sentence (en + es) that claims no server fault.
      return t('agent.messengerPrep.sendFailed')
    case 'rate':
      return es
        ? 'No se envió — está enviando demasiado rápido. Espere unos segundos.'
        : "Not sent — you're sending a little fast. Give it a few seconds."
    case 'offline':
      return es
        ? 'No se envió — parece estar sin conexión. Revise su conexión.'
        : 'Not sent — you appear to be offline. Check your connection.'
    default:
      return es
        ? 'No se envió — ocurrió un problema de nuestro lado. Su mensaje se guardó.'
        : 'Not sent — something went wrong on our end. Your message is kept.'
  }
}

// Reason-aware "didn't send" strip with a Retry, shown under the patient's own
// unsent message. Never styled as an agent reply.
function FailedSendNotice({ kind, language, t, retryLabel, onRetry }) {
  return (
    <div className="flex flex-col items-end gap-1 animate-[agentFadeUp_0.3s_ease]">
      <div className="max-w-[88%] flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-[12.5px] text-red-700 leading-snug">{agentErrorCopy(kind, language, t)}</p>
          <button onClick={onRetry} className="mt-1.5 text-[12px] font-semibold text-red-700 inline-flex items-center gap-1 hover:underline">
            <RotateCw className="w-3.5 h-3.5" /> {retryLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// reaches the doctor. The Edit button lets the patient adjust the distilled
// question before sending — the agent's distillation might miss nuance.
function MessengerPrepCard({ prep, caseId, msgRef, doctorName }) {
  const { t, language } = useI18n()
  const sendMessengerPrep = useAgentStore((s) => s.sendMessengerPrep)
  const [sending, setSending] = useState(false)
  const [editing, setEditing] = useState(false)
  const [edited, setEdited] = useState(prep.distilled_question)
  const [errKind, setErrKind] = useState(null)

  // v226.9 — reply mode: the patient is answering an open physician message;
  // this goes back into the physician's existing thread, not a new escalation.
  const isReply = prep.mode === 'reply'

  const handleSend = async () => {
    setSending(true)
    setErrKind(null)
    // Pass the edited text through so edits actually send (prior versions
    // dropped the edit and sent the original distilled text).
    const result = await sendMessengerPrep(caseId, msgRef, edited)
    setSending(false)
    if (!result.ok) {
      setErrKind(classifyAgentStatus(result.status, result.kind))
    }
  }

  // s120 (his ruling 2026-10-25): an answer to the physician's question is ORGANIZED by Orella and waits for the
  //   patient's tap on "Send to {{name}}". The card names the question it answers; "Label: value" lines read as a
  //   two-column list (the API's REPLY_PREP writes one line per thing the physician asked about).
  const question = isReply && typeof prep.question === 'string' ? prep.question.trim() : ''
  const shortQuestion = question.length > 80 ? question.slice(0, 79).trimEnd() + '…' : question
  const headingTpl = isReply
    ? (shortQuestion
        ? t('agent.answeringQuestion', { defaultValue: 'To {{name}} · answering "{{question}}"', name: doctorName, question: shortQuestion })
        : t('agent.messengerPrep.replyHeading', 'Reply to {{name}}'))
    : (t('agent.messengerPrep.heading') || 'Prepared for {{name}}')
  const sendTpl = t('agent.messengerPrep.send') || 'Send to {{name}}'
  const heading = headingTpl.replace('{{name}}', doctorName)
  const sendLabel = sendTpl.replace('{{name}}', doctorName)
  const lines = String(edited || '').split('\n').map((l) => l.trim()).filter(Boolean)
  const pairs = lines.map((l) => { const m = l.match(/^([^:]{1,32}):\s+(.+)$/); return m ? [m[1], m[2]] : null })
  const asPairs = isReply && lines.length > 1 && pairs.every(Boolean)

  return (
    <div className="ml-9 self-stretch my-1 animate-[agentFadeUp_0.3s_ease]" data-prep-mode={isReply ? 'reply' : 'escalate'}>
    {isReply && (
      <p className="text-[13px] text-[#15354F] mb-1.5" data-prep-lead="">
        {t('agent.organizedFor', { defaultValue: 'Here is your answer, organized for {{name}}:', name: doctorName })}
      </p>
    )}
    <div className={isReply
      ? 'border-[1.5px] border-[#46A8F9] bg-white rounded-2xl p-3.5'
      : 'border border-dashed border-[#46A8F9] bg-[#E9F8FF] rounded-2xl p-3.5'}>
      <div className="flex items-center gap-1.5 mb-2">
        <FileText size={13} className="text-[#15354F]" strokeWidth={2.5} />
        <span className="text-[11px] font-bold text-[#15354F] uppercase tracking-wide">
          {heading}
        </span>
      </div>

      {editing ? (
        <textarea
          value={edited}
          onChange={(e) => setEdited(e.target.value)}
          className="w-full bg-white border border-[#A7D9FC] rounded-lg p-2.5 text-[13px] text-[#15354F] min-h-[80px] mb-2.5 focus:outline-none focus:border-[#46A8F9]"
        />
      ) : (
        asPairs ? (
          <dl className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-2 gap-y-1.5 mb-2.5 text-[13px] leading-snug" data-prep-pairs="">
            {pairs.map(([k, v], pi) => (
              <div key={pi} className="contents">
                <dt className="text-gray-500">{k}</dt>
                <dd className="text-[#15354F] m-0">{v}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className="bg-white border border-[#A7D9FC] rounded-lg p-2.5 mb-2.5 text-[13px] text-[#15354F] leading-relaxed whitespace-pre-line">
            {edited}
          </div>
        )
      )}

      {errKind && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-2.5 py-2 mb-2.5">
          <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-red-700 leading-snug">{agentErrorCopy(errKind, language, t)}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleSend}
          disabled={sending}
          className="flex-1 bg-[#66D170] text-[#08331a] text-[13px] font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-opacity"
        >
          {sending ? <OrellaSpinner size={16} tone="inherit" /> : (errKind ? <RotateCw size={14} /> : <Send size={14} />)}
          {sending
            ? (t('agent.messengerPrep.sending') || 'Sending…')
            : (errKind
                ? (isReply
                    ? t('agent.messengerPrep.replyRetry', { defaultValue: 'Retry reply to {{name}}', name: doctorName })
                    : t('agent.messengerPrep.retry', { defaultValue: 'Retry send to {{name}}', name: doctorName }))
                : sendLabel)}
        </button>
        <button
          onClick={() => setEditing(!editing)}
          disabled={sending}
          className="bg-white text-[#15354F] text-[13px] font-semibold py-2 px-4 rounded-lg border border-[#A7D9FC] hover:bg-[#E9F8FF] disabled:opacity-50 flex items-center gap-1.5"
        >
          <Edit3 size={13} />
          {editing ? (t('agent.messengerPrep.done') || 'Done') : (t('agent.messengerPrep.edit') || 'Edit')}
        </button>
      </div>
    </div>
    </div>
  )
}


function AgentMessage({ msg, isLast, onStreamComplete, inline = false }) {
  const { t } = useI18n()

  // v220.1 — system pills (e.g. "Sent to your doctor")
  if (msg.role === 'system') {
    return (
      <div className="flex justify-center my-2 animate-[agentFadeUp_0.3s_ease]">
        <div className="text-[11px] text-slate-500 bg-white border border-slate-200 px-3 py-1 rounded-full">
          {msg.content}
        </div>
      </div>
    )
  }

  // v220.1 — MD-authored message bubble. Distinct identity: green border,
  // "From Dr. X" cap. Never confusable with the agent (ice-blue) or the
  // patient (navy). Authority hierarchy: MD bubble is ground truth.
  if (msg.role === 'md_message') {
    const fromName = msg.mdName || 'your physician'
    // s120 (Dr. Rami 2026-10-25, canvas "Orella App Chat — Round 1"): "If the doctor sends a message we should lead
    //   with an orella dialogue that says 'you have a new message from your doctor.' and the message from MD appears
    //   under that." One column, Orella's avatar; the physician's card keeps its green identity.
    const mdAvatar = inline ? 'w-10 h-10 rounded-xl' : 'w-7 h-7 rounded-lg'
    return (
      <div className="flex items-start gap-2 flex-row animate-[agentFadeUp_0.3s_ease]" data-md-lead="">
        <img src={orellaOIcon} alt="" className={`${mdAvatar} flex-shrink-0 object-contain`} />
        <div className="max-w-[85%] min-w-0 flex flex-col gap-1.5">
        <div className={`${DIALOGUE_BUBBLE} ${DIALOGUE_PAD} self-start`}>
          <p className={DIALOGUE_TEXT}>{t('agent.mdLead', { defaultValue: 'You have a new message from {{name}}.', name: fromName })}</p>
        </div>
        <div className="px-3.5 py-2.5 bg-white border-2 border-[#66D170] text-[#15354F] rounded-tr-2xl rounded-br-2xl rounded-bl-2xl rounded-tl-sm">
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-4 h-4 rounded-full bg-[#66D170] flex items-center justify-center">
              <Check size={9} className="text-white" strokeWidth={3} />
            </div>
            <span className="text-[10.5px] font-bold text-[#16A34A] uppercase tracking-wide">
              {(t('agent.fromMd') || 'From {{name}} · your physician').replace('{{name}}', fromName)}
            </span>
          </div>
          <p className={inline ? `${DIALOGUE_TEXT} line-clamp-3` : DIALOGUE_TEXT}>
            {msg.content}
          </p>
        </div>
        </div>
      </div>
    )
  }

  // FHOOK: system and md_message bubbles are returned above, with no hooks of their own beyond
  //   useI18n; every other message renders in AgentBubble, which owns the remaining hooks.
  //   Rules of hooks: no early return sits above a hook in either component.
  return <AgentBubble msg={msg} isLast={isLast} onStreamComplete={onStreamComplete} inline={inline} />
}

function AgentBubble({ msg, isLast, onStreamComplete, inline = false }) {
  const { t } = useI18n()
  const isUser = msg.role === 'user'
  // Only stream the latest assistant message (isLast) that hasn't been streamed yet
  const shouldStream = isLast && !isUser && !msg._streamed

  // v169.4.0: Split assistant messages on --- delimiter into separate bubbles
  const chunks = !isUser && msg.content
    ? msg.content.split(/\n---\n/).map(c => c.trim()).filter(Boolean)
    : [msg.content]
  const isChunked = chunks.length > 1

  // v176.2.0: Sequential chunk streaming — each chunk waits for the previous to finish
  // visibleChunks = how many bubbles are shown
  // streamingChunkIndex = which chunk is currently streaming (null = none)
  const [visibleChunks, setVisibleChunks] = useState(isChunked && isLast && !msg._streamed ? 1 : chunks.length)
  const [streamingChunkIndex, setStreamingChunkIndex] = useState(isChunked && isLast && !msg._streamed ? 0 : null)

  // When a chunk finishes streaming, show typing dots briefly then reveal next chunk
  const handleChunkStreamComplete = useCallback((chunkIndex) => {
    if (chunkIndex >= chunks.length - 1) {
      // Last chunk finished — all done
      setStreamingChunkIndex(null)
      onStreamComplete?.()
      return
    }
    // Show typing dots for the next chunk (current chunk stays, dots appear below)
    setStreamingChunkIndex(null) // briefly null = dots visible
    const timer = setTimeout(() => {
      setVisibleChunks(chunkIndex + 2) // reveal next chunk
      setStreamingChunkIndex(chunkIndex + 1) // stream it
    }, 800) // 800ms pause with dots between chunks
    return () => clearTimeout(timer)
  }, [chunks.length, onStreamComplete])

  // Notify stream complete for single-chunk messages handled by non-chunked path
  // (chunked path calls onStreamComplete via handleChunkStreamComplete)

  const bubbleClass = isUser
    ? 'bg-[#15354F] text-white rounded-tl-2xl rounded-bl-2xl rounded-br-2xl rounded-tr-sm'
    : msg.isEmergency
      ? 'bg-red-50 border border-red-200 text-red-800 rounded-tr-2xl rounded-br-2xl rounded-bl-2xl rounded-tl-sm'
      : DIALOGUE_BUBBLE

  // v230.4.6: Inline (home) dialogue uses the 40px header-size O to match the
  // OrellaPageHeader on the other tabs; the overlay chat keeps its 28px chat avatar.
  const avatarClass = inline ? 'w-10 h-10 rounded-xl' : 'w-7 h-7 rounded-lg'
  const spacerClass = inline ? 'w-10' : 'w-7'

  // Single bubble (user messages, short responses, already-streamed)
  if (!isChunked) {
    return (
      <div>
        <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'} ${isLast ? 'animate-[agentFadeUp_0.3s_ease]' : ''}`}>
          {!isUser && (
            <img src={orellaOIcon} alt="Orella" className={`${avatarClass} flex-shrink-0 object-contain`} />
          )}
          <div className={`max-w-[82%] ${DIALOGUE_PAD} ${bubbleClass}`}>
            {msg.isEmergency && (
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle size={14} className="text-red-500" />
                <span className="text-[11px] font-bold text-red-600 uppercase tracking-wide">{t('agent.emergency')}</span>
              </div>
            )}
            {shouldStream ? (
              <StreamingText text={msg.content} onComplete={onStreamComplete} />
            ) : (
              <p className={DIALOGUE_TEXT}>{msg.content}</p>
            )}
          </div>
        </div>
        {!isUser && !msg.isEmergency && !shouldStream && (
          <Sources sources={msg.sources} className={inline ? 'ml-12' : 'ml-9'} />
        )}
      </div>
    )
  }

  // Chunked bubbles — sequential streaming: each chunk streams fully before next appears
  return (
    <div className="space-y-2">
      {chunks.slice(0, visibleChunks).map((chunk, i) => (
        <div key={i} className={`flex items-end gap-2 flex-row animate-[agentFadeUp_0.3s_ease]`}>
          {i === 0 ? (
            <img src={orellaOIcon} alt="Orella" className={`${avatarClass} flex-shrink-0 object-contain`} />
          ) : (
            <div className={`${spacerClass} flex-shrink-0`} /> /* spacer to align with first bubble */
          )}
          <div className={`max-w-[82%] ${DIALOGUE_PAD} ${bubbleClass}`}>
            {shouldStream && streamingChunkIndex === i ? (
              <StreamingText text={chunk} onComplete={() => handleChunkStreamComplete(i)} />
            ) : (
              <p className={DIALOGUE_TEXT}>{chunk}</p>
            )}
          </div>
        </div>
      ))}
      {/* Typing dots while waiting for next chunk — shown between chunks */}
      {shouldStream && visibleChunks < chunks.length && streamingChunkIndex === null && (
        <div className="flex items-end gap-2 flex-row animate-[agentFadeUp_0.3s_ease]">
          <div className={`${spacerClass} flex-shrink-0`} />
          <div className={`${DIALOGUE_PAD} ${DIALOGUE_BUBBLE}`}>
            <div className="flex gap-1.5">
              <span className="w-[5px] h-[5px] rounded-full bg-[#A7D9FC] animate-[agentDotPulse_1.2s_ease-in-out_infinite]" />
              <span className="w-[5px] h-[5px] rounded-full bg-[#A7D9FC] animate-[agentDotPulse_1.2s_ease-in-out_infinite_0.2s]" />
              <span className="w-[5px] h-[5px] rounded-full bg-[#A7D9FC] animate-[agentDotPulse_1.2s_ease-in-out_infinite_0.4s]" />
            </div>
          </div>
        </div>
      )}
      {!msg.isEmergency && visibleChunks >= chunks.length && streamingChunkIndex === null && (
        <Sources sources={msg.sources} className="ml-9" />
      )}
    </div>
  )
}

// Quick suggestion chips
// v206.9: Chips are now typed — 'answer_options' render as filled navy (tap-to-answer),
// 'follow_ups' keep the outlined light style (tap-to-explore). This visual distinction
// tells the user whether a chip answers the question the AI just asked, or opens a new thread.
function QuickSuggestions({ suggestions, kind, onSelect, disabled }) {
  if (!suggestions || suggestions.length === 0) return null
  const isAnswerOptions = kind === 'answer_options'
  const chipClass = isAnswerOptions
    ? "text-[12px] font-medium text-white bg-[#15354F] border border-[#15354F] rounded-full px-3 py-1.5 hover:bg-[#0F2838] active:bg-[#0A1F2D] transition-colors disabled:opacity-40"
    : "text-[12px] font-medium text-[#15354F] bg-white border border-[#A7D9FC] rounded-full px-3 py-1.5 hover:bg-[#E9F8FF] active:bg-[#D4EFFF] transition-colors disabled:opacity-40"
  return (
    <div className="flex flex-wrap gap-1.5 px-1 animate-[agentFadeUp_0.3s_ease]">
      {suggestions.map((s, i) => (
        <button key={i} onClick={() => !disabled && onSelect(s)} disabled={disabled}
          className={chipClass}
        >{s}</button>
      ))}
    </div>
  )
}


/**
 * OrellaChatAgent — main chat interface as a bottom sheet portal
 */
// v242.66.0: how tall the chat box may grow before it scrolls. 120px was
// ~4 lines and a 60-second voice transcript is far longer than that.
const AGENT_INPUT_MAX_H = 200

export default function OrellaChatAgent({ isOpen, onClose, caseId, caseContext = {}, onNavigate: parentNavigate, initialMessage, onInitialMessageConsumed, fullscreen = false, inline = false, interstitial = null, composer = 'default', beforeOpen = null, openTone = 'navy', placeholder, startCollapsed = false }) {
  const { t, language } = useI18n()
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false) // v132.6.4: tracks word-by-word reveal
  // s101 P1.5 (Alex, Build 65 x_12: "Collapse the last chat with Orella when I open the case"): Case Home
  //   passes startCollapsed, so a returning patient sees the one-line peek, not the whole thread.
  const [threadCollapsed, setThreadCollapsed] = useState(!!startCollapsed) // v234.1.0: collapse the inline (home) thread
  const [showEarlier, setShowEarlier] = useState(false)         // v234.1.0: fold older inline messages
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  // s120 chat round 1 (his TestFlight notes 2026-10-25: "I cannot scroll up to read prior messages ... When scrolling
  //   up it automatically goes to end of thread"; "The keyboard cant be minimized while I review messages above it").
  //   The thread follows new messages ONLY while the patient is at the bottom (or just sent one); scrolled up, new
  //   messages raise a "N new messages" pill instead of pulling them down. A downward swipe on the thread, or the
  //   hide-keyboard button, puts the keyboard away.
  const nearBottomRef = useRef(true)
  const seenCountRef = useRef(0)
  const touchYRef = useRef(null)
  const [unseen, setUnseen] = useState(0)
  const [inputFocused, setInputFocused] = useState(false)
  const { openAgent } = useAgent()
  const routerNavigate = useNavigate()
  // v132.6.2: Prefer parent-provided navigate (stays mounted) over portal's own (unmounts with sheet)
  const navigateRef = useRef(parentNavigate || routerNavigate)
  navigateRef.current = parentNavigate || routerNavigate

  // v169.3.0: Live voice-to-text in agent chat
  // v242.66.0: whatever the patient had already TYPED is preserved. onTranscript
  // hands back the VOICE-ONLY accumulation, and assigning it wholesale erased a
  // typed prefix at the first commit.
  const voiceBaseRef = useRef('')
  const { isListening: agentListening, isTranscribing: agentTranscribing, interim: agentInterim, toggle: agentToggleVoice, error: agentVoiceError, clearError: agentClearVoiceError, mode: agentVoiceMode } = useVoiceInput({
    onTranscript: (text) => setInput([voiceBaseRef.current, text].filter(Boolean).join(' ')),
    language,
    maxDuration: 60,
  })

  // v242.66.0: capture the typed prefix at the moment recording STARTS.
  const handleVoiceToggle = useCallback(() => {
    if (!agentListening) voiceBaseRef.current = input
    agentToggleVoice()
  }, [agentListening, input, agentToggleVoice])

  // v242.66.0 — ★ WHAT THE TEXTAREA SHOWS WHILE RECORDING.
  //
  // Amazon Transcribe emits PARTIAL results continuously and FINAL ones every few
  // seconds (lib/transcribeStream.js: r.IsPartial -> onPartial, else onCommit).
  // Only the finals ever reached `input`; the partial went to a `truncate`d status
  // line under the box, so the patient watched their words appear one clipped line
  // at a time and land in the box in bursts.
  //
  // `input` stays the committed value — it is what handleSend sends — and the
  // partial is appended for DISPLAY only. On commit the partial clears and `input`
  // grows by the same words, so the text never flickers or double-renders.
  const liveValue = agentListening && agentInterim
    ? [input, agentInterim].filter(Boolean).join(' ')
    : input

  // Stores
  const ensureConversation = useAgentStore((s) => s.ensureConversation)
  const sendMessage = useAgentStore((s) => s.sendMessage)
  const retrySend = useAgentStore((s) => s.retrySend)
  const acceptAction = useAgentStore((s) => s.acceptAction)
  const dismissAction = useAgentStore((s) => s.dismissAction)
  const markStreamed = useAgentStore((s) => s.markStreamed)
  const hydrateCase = useAgentStore((s) => s.hydrateCase)   // v242.156.0 · CASE-CHAT-PERSIST-1
  const isLoading = useAgentStore((s) => s.loadingCaseId === caseId)
  const loading = isLoading // v132.6.2: direct selector on loadingCaseId for proper reactivity
  const { profile, updateProfile, syncSection } = useProfileStore()

  /* ★★★ v242.157.0 — AGENT-RENDER-PHASE-SET-1. THIS LINE USED TO BE
     `getConversation(caseId, caseContext)`, and that function wrote to the store —
     twice — so every render of this component performed a store write DURING RENDER.
     D221 removed the disagreement it caused; the violation stayed, survivable only
     while exactly one mounted component did it. A second opener re-arms the crash.

     ★★ THE SEED IS NOW EFFECT-SCOPED AND THE READ IS A SUBSCRIPTION. `useLayoutEffect`
     rather than `useEffect` deliberately: it runs after render but BEFORE the browser
     paints, so the one frame in which the thread has not been seeded yet is never
     shown. With `useEffect` the patient would see an empty chat flash open.

     ★ NO DEPENDENCY ARRAY, ALSO DELIBERATELY. `ensureConversation` is idempotent — it
     writes only when there is no conversation or when a reseed condition fires — and
     running it after every render is EXACTLY what the old render-body call did. A
     dependency array here would be a guess about which fields of `caseContext` the
     seeding logic reads, and getting that guess wrong is a stale greeting nobody can
     reproduce. Same behaviour, legal phase. */
  useLayoutEffect(() => {
    ensureConversation(caseId, caseContext)
  })

  const conversation = useAgentStore((s) => s.conversations[caseId])
  /* ★★★ v242.156.0 — SUBSCRIBED, NOT ONLY DERIVED, AND THIS IS CHAT-SKIP-DEAD-1 ONE ROW
     LATER. The comment forty lines below already says it: `conversation` comes from
     CALLING a stable function, so nothing here was ever subscribed to `conversations`.
     `hydrateCase` writes MESSAGES and nothing else — it does not touch pendingActions,
     which is the one slice this component does subscribe to, and it does not touch the
     profile store, which is what made `Add to profile` look fine by accident. Without
     this line the record would load into the store correctly and never reach the
     screen: a patient reopening a case would see an empty thread, which is
     indistinguishable from a feature that was never built.
     ★ v242.157.0 — THIS PARAGRAPH USED TO SAY "it resolves to the SAME array on first
       render, because getConversation has already seeded the store by the time this
       reads it". THAT IS NO LONGER TRUE and is corrected rather than left (§1dn(e)):
       the seed now happens in the layout effect above, so on the very first render
       both this selector and `conversation` are undefined and `messages` is []. The
       effect runs before paint, so nothing is shown in that state — but a reader who
       trusted the old sentence would conclude the empty case is unreachable. */
  const subscribedMessages = useAgentStore((s) => s.conversations[caseId]?.messages)
  const messages = subscribedMessages || conversation?.messages || []
  const suggestions = conversation?.suggestions || []
  const suggestionsKind = conversation?.suggestionsKind || null // v206.9: 'answer_options' | 'follow_ups' | null
  // ★★★ v242.101.0 — `CHAT-SKIP-DEAD-1`. THIS LINE IS THE WHOLE BUG.
  //
  // `conversation` USED TO COME FROM CALLING getConversation(), and the only thing
  // this component selected from the store was that FUNCTION — a stable reference. So
  // nothing here was ever subscribed to `conversations`, and a store write that
  // touched only that slice re-rendered NOTHING.
  // ★ v242.157.0: `conversation` is a real selector now (AGENT-RENDER-PHASE-SET-1), so
  //   this particular route to the bug is closed — the paragraph is kept because the
  //   CLASS is not, and the lines below are what the class cost.
  //
  // Skip → dismissAction() → the store DID set status:'dismissed' → the card
  // kept rendering, because no subscribed value had changed. The state was
  // right and the screen was stale, which is indistinguishable from a dead
  // button. ★★ `Add to profile` looked fine only by ACCIDENT: its handler also
  // writes useProfileStore, which IS subscribed, and that re-render happened to
  // re-read the new status on its way past.
  //
  // Subscribe to the array the cards actually render from.
  const pendingActions = useAgentStore((s) => s.conversations[caseId]?.pendingActions) || EMPTY_ACTIONS
  const failedSend = conversation?.failedSend || null

  /* ═══ v242.156.0 · CASE-CHAT-PERSIST-1 — PULL THE RECORD BACK ════════════════════
     D348: the case chat is the window with the important case information in it, and it
     was stored nowhere. It is stored now, and this is where it comes back.
     ★ AFTER the render that seeds the opener, deliberately: `hydrateCase` appends to
       messages[0] rather than replacing the thread, because on Case Home messages[0] is
       the HERO LINE the screen computed and the server never saw it.
     ★ The store owns every guard — real case only, once per case per account, and never
       over a thread the patient is already using. This is a call site, not a policy. */
  useEffect(() => { hydrateCase(caseId) }, [caseId, hydrateCase])

  // Auto-scroll — s120: only while the patient is at the bottom, or right after they sent a message.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const last = messages[messages.length - 1]
    if (nearBottomRef.current || last?.role === 'user') {
      el.scrollTop = el.scrollHeight
      nearBottomRef.current = true
      setUnseen(0)
    } else if (messages.length > seenCountRef.current) {
      const added = messages.length - seenCountRef.current
      setUnseen((n) => n + added)
    }
    seenCountRef.current = messages.length
  }, [messages.length, loading, pendingActions.length, isStreaming])

  // s120: opening the sheet starts at the latest message.
  useEffect(() => {
    if (!isOpen || inline) return
    nearBottomRef.current = true
    setUnseen(0)
    const id = requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight })
    return () => cancelAnimationFrame(id)
  }, [isOpen, inline])

  const handleThreadScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    nearBottomRef.current = near
    if (near) setUnseen(0)
  }, [])
  const jumpToLatest = useCallback(() => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    nearBottomRef.current = true
    setUnseen(0)
  }, [])
  const handleThreadTouchStart = useCallback((e) => {
    touchYRef.current = e.touches && e.touches[0] ? e.touches[0].clientY : null
  }, [])
  const handleThreadTouchMove = useCallback((e) => {
    const y0 = touchYRef.current
    const y = e.touches && e.touches[0] ? e.touches[0].clientY : null
    if (y0 != null && y != null && y - y0 > 12 && inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.blur()
    }
  }, [])
  const hideKeyboard = useCallback(() => { inputRef.current?.blur() }, [])
  // s120: the sheet closes on a downward drag of its handle / header.
  const sheetDragRef = useRef(null)
  const handleSheetDragStart = useCallback((e) => {
    sheetDragRef.current = e.touches && e.touches[0] ? e.touches[0].clientY : null
  }, [])
  const handleSheetDragEnd = useCallback((e) => {
    const y0 = sheetDragRef.current
    const y = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : null
    sheetDragRef.current = null
    if (y0 != null && y != null && y - y0 > 70) onClose?.()
  }, [onClose])

  // v132.6.4: Start streaming when a new assistant message arrives
  const prevMsgCount = useRef(messages.length)
  useEffect(() => {
    if (messages.length > prevMsgCount.current) {
      const lastMsg = messages[messages.length - 1]
      if (lastMsg?.role === 'assistant' && !lastMsg._streamed) {
        setIsStreaming(true)
      }
      // v234.1.0: a new message arrived — keep it visible. s101: only a message that is NEW NOW —
      //   the saved thread loading in after mount also raises the count, and must not undo
      //   startCollapsed. A message with no timestamp counts as new (the send path).
      const ts = Date.parse(lastMsg?.timestamp || '')
      if (!Number.isFinite(ts) || Date.now() - ts < 2 * 60 * 1000) setThreadCollapsed(false)
    }
    prevMsgCount.current = messages.length
  }, [messages.length])

  const handleStreamComplete = useCallback(() => {
    setIsStreaming(false)
    // v181.0.0: Mark last message as streamed in store so it doesn't re-stream on reopen
    markStreamed(caseId)
    // Scroll to bottom after stream finishes to show nav cards + suggestions
    setTimeout(() => {
      if (scrollRef.current && nearBottomRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }, 50)
  }, [caseId, markStreamed])

  // Focus input
  useEffect(() => {
    // s120: never on a touch screen — focusing on open threw the keyboard over the thread he opened the sheet to read.
    const finePointer = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: fine)').matches
    if (isOpen && !inline && inputRef.current && finePointer) {
      const timer = setTimeout(() => inputRef.current?.focus(), 350)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // v220.1 — Load MD-authored messages and merge into the conversation
  // when the chat opens (or case changes). loadCaseMessages is a no-op
  // for non-UUID case ids ('home-chat', 'profile', etc.).
  const loadCaseMessages = useAgentStore((s) => s.loadCaseMessages)
  useEffect(() => {
    if (!isOpen || !caseId) return
    loadCaseMessages(caseId)
  }, [isOpen, caseId, loadCaseMessages])

  // v151.1.0: Auto-send initial message when agent opens with one
  useEffect(() => {
    if (isOpen && initialMessage && !loading) {
      const timer = setTimeout(() => {
        // v213.10: Persist initial home-chat message as seed for case's voiceTextInput
        try {
          if (caseId === 'home-chat') {
            const existingSeed = sessionStorage.getItem('orella_home_triage_input')
            if (!existingSeed) {
              sessionStorage.setItem('orella_home_triage_input', initialMessage)
              sessionStorage.setItem('orella_home_triage_ts', String(Date.now()))
            }
          }
        } catch {}
        sendMessage(caseId, initialMessage, caseContext)
        if (onInitialMessageConsumed) onInitialMessageConsumed()
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [isOpen, initialMessage])

  // v132.6.4: Keep scrolling down during word-by-word streaming
  useEffect(() => {
    if (!isStreaming) return
    const interval = setInterval(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }, 80)
    return () => clearInterval(interval)
  }, [isStreaming])

  // v132.6.0: Auto-resize textarea (AGT-7)
  // v242.66.0 — ★ THIS WAS THE WHOLE BUG. Resizing lived in the onChange handler,
  // so it ran on TYPING ONLY. Voice writes through setInput and never fired it, so
  // the box stayed at its rows={1} height with overflow:hidden and showed exactly
  // one clipped line no matter how much had been transcribed.
  //
  // The fix is the shape DescribeVoice.jsx already uses: resize from an effect
  // keyed on the VALUE, so every writer — typing, voice, paste, programmatic —
  // gets it for free. A handler cannot cover a writer that does not go through it.
  const handleInputChange = useCallback((e) => {
    setInput(e.target.value)
  }, [])

  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    const next = Math.min(el.scrollHeight, AGENT_INPUT_MAX_H)
    el.style.height = next + 'px'
    // Only scroll once the cap is actually reached. The previous rule keyed on
    // NEWLINE COUNT, and a voice transcript is one long line with zero newlines —
    // so overflow was permanently 'hidden' and long transcripts were clipped
    // invisibly, with no scrollbar to hint that anything was missing.
    el.style.overflowY = el.scrollHeight > AGENT_INPUT_MAX_H ? 'auto' : 'hidden'
  }, [liveValue])

  const handleSend = useCallback(() => {
    const text = input.trim()
    if (!text || loading) return
    setInput('')
    // Reset textarea height after send
    if (inputRef.current) inputRef.current.style.height = 'auto'
    // v213.10: When the user types their first message in a home-chat session
    // (no real case yet), persist it as a seed for the eventual case's
    // voiceTextInput. DescribeVoice consumes this on mount. Only the FIRST
    // user message seeds — subsequent agent dialogue is conversational and
    // doesn't represent the chief complaint. Stale seeds (>30 min) are ignored
    // by the consumer.
    try {
      if (caseId === 'home-chat') {
        const existingSeed = sessionStorage.getItem('orella_home_triage_input')
        if (!existingSeed) {
          sessionStorage.setItem('orella_home_triage_input', text)
          sessionStorage.setItem('orella_home_triage_ts', String(Date.now()))
        }
      }
    } catch {}
    sendMessage(caseId, text, caseContext)
  }, [input, loading, caseId, caseContext, sendMessage])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }, [handleSend])

  /** Apply a profile action to the profile store */
  const handleAcceptAction = useCallback(async (actionId) => {
    const action = acceptAction(caseId, actionId)
    if (!action) return

    const d = action.data || {}

    try {
      switch (action.action) {
        case 'add_allergy': {
          const current = profile?.allergies || {}
          const category = action.category || 'medication'
          const listKey = category === 'food' ? 'foodList' : category === 'contact' ? 'contactList' : 'medList'
          const hasKey = category === 'food' ? 'hasFoodAllergies' : category === 'contact' ? 'hasContactAllergies' : 'hasMedAllergies'
          const existing = current[listKey] || []
          const newEntry = { name: d.name, severity: d.severity || 'Unknown', reaction: d.reaction || '', id: d.name.toLowerCase().replace(/\s+/g, '-') }
          const updated = { ...current, [hasKey]: true, [listKey]: [...existing, newEntry], completed: true }
          await syncSection('allergies', updated)
          break
        }
        case 'add_medication': {
          const current = profile?.medications || {}
          const existing = current.list || []
          const newMed = { name: d.name, dosage: d.dosage || '', frequency: d.frequency || t('agent.onceDaily'), purpose: d.purpose || '', dateStarted: '' }
          const updated = { hasMedications: true, list: [...existing, newMed], completed: true }
          await syncSection('medications', updated)
          break
        }
        case 'add_condition': {
          const current = profile?.medicalHistory || {}
          const existing = toConditionList(current.conditions)
          const newCond = { id: d.name.toLowerCase().replace(/\s+/g, '-'), name: d.name, yearDiagnosed: d.yearDiagnosed || '', status: d.status || 'Active' }
          const updated = { ...current, noConditions: false, conditions: [...existing, newCond], completed: true }
          await syncSection('medicalHistory', updated)
          break
        }
        case 'add_surgery': {
          const current = profile?.medicalHistory || {}
          const existing = current.surgeries || []
          const newSurgery = { id: d.name.toLowerCase().replace(/\s+/g, '-'), name: d.name, year: d.year || '' }
          const updated = { ...current, noSurgeries: false, surgeries: [...existing, newSurgery], completed: true }
          await syncSection('medicalHistory', updated)
          break
        }
        case 'add_family_condition': {
          const current = profile?.familyHistory || {}
          const existing = toConditionList(current.otherConditions)
          const newFam = { id: `custom-${Date.now()}`, name: d.conditionName, mother: d.relatives?.includes('mother') || false, father: d.relatives?.includes('father') || false, sibling: d.relatives?.includes('sibling') || false, grandparent: d.relatives?.includes('grandparent') || false }
          const updated = { ...current, otherConditions: [...existing, newFam], completed: true }
          await syncSection('familyHistory', updated)
          break
        }
        case 'set_social': {
          // v226.9 — social/lifestyle is a flat structured object (not a list).
          // Merge only the fields the agent supplied; never clobber the rest.
          const current = profile?.socialLifestyle || {}
          const allowed = ['smoking', 'smokingFrequency', 'alcohol', 'alcoholFrequency', 'cannabis', 'cannabisFrequency', 'cannabisMethod', 'exercise', 'occupation']
          const patch = {}
          for (const k of allowed) {
            if (d[k] !== undefined && d[k] !== null && d[k] !== '') patch[k] = d[k]
          }
          const updated = { ...current, ...patch, completed: true }
          await syncSection('socialLifestyle', updated)
          break
        }
      }
    } catch (err) {
      console.error('[Agent] Failed to save profile action:', err)
    }
  }, [caseId, profile, acceptAction, syncSection])

  const handleDismissAction = useCallback((actionId) => {
    dismissAction(caseId, actionId)
  }, [caseId, dismissAction])

  // v206.3: Handle navigation — supports case_continue (sets currentCase before navigating)
  const handleNavigate = useCallback((routeOrNav) => {
    onClose?.()  // inline mode has no close handler — guard so navigation still fires
    setTimeout(() => {
      try {
        // Special case: continue a specific case by id
        if (typeof routeOrNav === 'object' && routeOrNav?.action === 'continue_case') {
          const targetCase = useCaseStore.getState().cases.find(c => c.id === routeOrNav.caseId)
          if (targetCase) {
            useCaseStore.getState().setCurrentCase(targetCase)
            navigateRef.current('/case/home', { state: { returnTo: '/home' } })
          } else {
            console.warn('[Agent] continue_case: case not found', routeOrNav.caseId)
            navigateRef.current('/cases')
          }
          return
        }
        navigateRef.current(routeOrNav)
      } catch (e) { console.warn('[Agent] Navigate error:', e) }
    }, 200)
  }, [onClose])

  // v213.3 (C-A-185): If the agent suggested a screen (msg.navigation set on the
  // last assistant message) and the user taps an affirmation chip ("Yes…",
  // "Start…", "Let's…", "Take me…", "Go"), perform the navigation directly
  // instead of echoing the text back to the AI. Otherwise fall through to the
  // normal "send to AI" path.
  const handleSuggestionSelect = useCallback((text) => {
    if (loading) return

    const AFFIRMATION_PREFIXES = ['yes', 'start', "let's", 'lets', 'take me', 'go ', 'open', 'sure', 'ok ', 'okay']
    const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null
    if (lastMsg && lastMsg.role === 'assistant' && lastMsg.navigation?.screen) {
      const lower = String(text || '').trim().toLowerCase()
      const matchesAffirmation = AFFIRMATION_PREFIXES.some(p => lower === p.trim() || lower.startsWith(p))
      if (matchesAffirmation) {
        const nav = lastMsg.navigation
        if (nav.screen === 'case_continue' && nav.caseId) {
          handleNavigate({ action: 'continue_case', caseId: nav.caseId })
          return
        }
        const route = SCREEN_ROUTES[nav.screen]
        if (route) {
          handleNavigate(route)
          return
        }
      }
    }
    sendMessage(caseId, text, caseContext)
  }, [loading, caseId, caseContext, sendMessage, messages, handleNavigate])

  // s120 round 2: the Home pill's mic opens the drawer RECORDING (once per opening).
  const voiceStartedRef = useRef(false)
  useEffect(() => {
    if (!isOpen || inline) { voiceStartedRef.current = false; return }
    if (caseContext?.openedFrom !== 'voice' || voiceStartedRef.current) return
    voiceStartedRef.current = true
    const id = setTimeout(() => { try { handleVoiceToggle() } catch { /* the mic stays one tap away */ } }, 400)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inline, caseContext?.openedFrom])

  if (!isOpen) return null

  // s120 (his ruling 2026-10-25): "If the MD asked a question the response should be sent to the MD by default —
  //   orella organizes the response then sends to the MD once its all organized" (the patient taps Send). While the
  //   physician's message is the latest word in the thread, the box asks the patient to answer them; while Orella's
  //   organized answer waits for the tap, it asks for anything else to add.
  let openMd = null
  for (let k = messages.length - 1; k >= 0; k--) {
    const m = messages[k]
    if (m.role === 'user') break
    if (m.role === 'md_message') { openMd = m; break }
  }
  const lastMsgAll = messages[messages.length - 1]
  const pendingReply = !!(lastMsgAll && lastMsgAll.role === 'assistant' && lastMsgAll.messengerPrep
    && !lastMsgAll._prepSent && lastMsgAll.messengerPrep.mode === 'reply')
  const doctorLabel = caseContext?.doctorName || (openMd && openMd.mdName) || ''
  const hasMdInThread = messages.some((m) => m.role === 'md_message')
  const composerPlaceholder = openMd
    ? t('agent.answerPlaceholder', { defaultValue: 'Answer {{name}}…', name: openMd.mdName || doctorLabel })
    : pendingReply && doctorLabel
      ? t('agent.addMoreFor', { defaultValue: 'Add anything else for {{name}}…', name: doctorLabel })
      : (placeholder || (hasMdInThread ? t('agent.messagePlaceholder', 'Message…') : t('agent.typeQuestion')))

  // Build a map of which message indices have actions
  const actionsByTimestamp = {}
  pendingActions.forEach(a => {
    // Match actions to the message that generated them by finding the closest assistant message
    const ts = parseInt(a.id?.split('-')[0]) || 0
    if (!actionsByTimestamp[ts]) actionsByTimestamp[ts] = []
    actionsByTimestamp[ts].push(a)
  })

  // v223.5.0 — Inline mode: chat lives in the page body (home hero); the page
  // scrolls, no overlay/portal/backdrop. Reuses the same message rendering,
  // send logic and voice input as the bottom sheet for full parity.
  // `interstitial` renders between the thread and the input (home MD notices).
  // Separate component instance from the provider's bottom-sheet, so refs/state
  // don't collide (only one branch renders per instance).
  if (inline) {
    // v234.1.0: the home inline thread can grow long — give it a collapse bar
    // (tucks the whole thread to a one-line peek) and fold older messages behind
    // a "show earlier" link once it exceeds a few turns.
    // ★ PROD-1157: the fold lives in agentPrepRef.foldThread, and `visibleOffset` turns a drawn
    //   position back into the FULL-list one — a prep card must never be named by its folded index.
    const hasThread = messages.length >= 2
    // s120 (his canvas comment 2026-10-25): "instead of having the chat typed here, we should have an open chat that
    //   opens the drawer". composer="open" draws the latest two messages and an Open chat button; typing happens in
    //   the bottom sheet (GlobalAgentProvider's instance, same caseId, same stored thread).
    const openOnly = composer === 'open'
    const folded3 = foldThread(messages, showEarlier)
    // s120 round 2 (canvas "Orella App Chat — Round 2", approved 2026-10-28): Home (master) shows Orella's latest
    //   line; Case Home shows the opener FIRST ("should be the first dialogue of the chat everything else opens
    //   underneath") and the latest message under it (a physician message clamped to 3 lines).
    const homeCard = openOnly && caseId === 'master'
    const openVisible = homeCard
      ? { folded: false, visible: messages.slice(-1), offset: Math.max(0, messages.length - 1), earlierCount: 0 }
      : messages.length <= 2
        ? { folded: false, visible: messages, offset: 0, earlierCount: 0 }
        // offset makes the LATEST message's index exact (visibleOffset + 1 = its full-list index), which is the only
        //   one that can carry a prep card; the opener never does.
        : { folded: false, visible: [messages[0], messages[messages.length - 1]], offset: messages.length - 2, earlierCount: 0 }
    const { folded: foldOlder, visible: visibleMessages, offset: visibleOffset, earlierCount } = openOnly
      ? openVisible
      : folded3
    const collapsedNow = openOnly ? false : threadCollapsed
    const lastMsg = messages[messages.length - 1]
    const peekText = typeof lastMsg?.content === 'string' ? lastMsg.content : ''
    return (
      <div className="flex flex-col">
        {hasThread && !homeCard && (
          <button
            onClick={() => (openOnly ? openAgent() : setThreadCollapsed(c => !c))}
            className="w-full flex items-center gap-2.5 bg-[#F6FBFF] border border-[#A7D9FC] rounded-xl px-3 py-2.5 mb-3 active:scale-[0.99] transition-transform"
          >
            <img src={orellaOIcon} alt="" className="w-[22px] h-[22px] rounded-md flex-shrink-0 object-contain" />
            <span className="text-[13px] font-bold text-[#15354F]">{t('home.chatWithOrella') || 'Chat with Orella'}</span>
            {openOnly && openMd ? (
              <span className="ml-auto text-[11px] font-bold text-[#16A34A] bg-[#EAF8EE] border border-[#BFE8CB] rounded-lg px-2 py-0.5 truncate">
                {t('agent.newFrom', { defaultValue: 'New from {{name}}', name: openMd.mdName || doctorLabel })}
              </span>
            ) : (
              <span className="ml-auto text-[11px] font-bold text-[#3A7BBF] bg-white border border-[#A7D9FC] rounded-lg px-2 py-0.5">
                {messages.length} {t('home.messagesLabel') || 'messages'}
              </span>
            )}
            {openOnly
              ? <ChevronRight size={16} className="text-[#46A8F9] flex-shrink-0" />
              : threadCollapsed
                ? <ChevronDown size={16} className="text-[#46A8F9] flex-shrink-0" />
                : <ChevronUp size={16} className="text-[#46A8F9] flex-shrink-0" />}
          </button>
        )}

        {collapsedNow && hasThread ? (
          <p className="text-[12.5px] text-gray-500 italic mb-1 ml-1 truncate">"{peekText}"</p>
        ) : (
        <div className="space-y-3">
          {foldOlder && (
            <button
              onClick={() => setShowEarlier(true)}
              className="w-full text-center text-[12px] font-bold text-[#46A8F9] py-1.5 active:underline"
            >
              ↑ {t('home.showEarlierMessages', { count: earlierCount }) || `Show earlier messages (${earlierCount})`}
            </button>
          )}
          {visibleMessages.map((msg, i) => {
            const msgTs = msg.timestamp
            const isLastMsg = i === visibleMessages.length - 1
            const actionsForMsg = pendingActions.filter(a => {
              const aTs = parseInt(a.id?.split('-')[0]) || 0
              return Math.abs(aTs - msgTs) < 500
            })
            return (
              <div key={`${msg.timestamp}-${i}`}>
                <AgentMessage msg={msg} isLast={isLastMsg} onStreamComplete={handleStreamComplete} inline />
                {msg.role === 'assistant' && !(isLastMsg && isStreaming) && actionsForMsg.map(action => (
                  <ProfileActionCard
                    key={action.id}
                    action={action}
                    onAccept={handleAcceptAction}
                    onDismiss={handleDismissAction}
                  />
                ))}
                {msg.role === 'assistant' && msg.navigation && !(isLastMsg && isStreaming) && (
                  <NavigationCard navigation={msg.navigation} onNavigate={handleNavigate} />
                )}
                {msg.role === 'assistant' && msg.messengerPrep && !msg._prepSent && !(isLastMsg && isStreaming) && (
                  <MessengerPrepCard
                    prep={msg.messengerPrep}
                    caseId={caseId}
                    msgRef={prepMsgRef(msg, visibleOffset + i)}
                    doctorName={caseContext?.doctorName || 'your physician'}
                  />
                )}
              </div>
            )
          })}

          {failedSend && !loading && (
            <FailedSendNotice
              kind={failedSend.kind}
              language={language}
              t={t}
              retryLabel={t('agent.retry', 'Retry')}
              onRetry={() => retrySend(caseId)}
            />
          )}

          {loading && (
            <div className="flex items-end gap-2 animate-[agentFadeUp_0.3s_ease]">
              <img src={orellaOIcon} alt="" className="w-10 h-10 rounded-xl flex-shrink-0 object-contain" />
              <div className={`${DIALOGUE_BUBBLE} ${DIALOGUE_PAD}`}>
                <TypingDots />
              </div>
            </div>
          )}

          {!openOnly && !loading && !isStreaming && suggestions.length > 0 && (
            <div className="ml-9 animate-[agentFadeUp_0.3s_ease]">
              <QuickSuggestions suggestions={suggestions} kind={suggestionsKind} onSelect={handleSuggestionSelect} disabled={loading} />
            </div>
          )}
        </div>
        )}

        {interstitial}

        {homeCard ? (
          <div className="mt-3" data-home-chat-pill="">
            {/* s120 round 2 H1: Home's chat is the same drawer as Case Home's — one navy pill opens it; the mic
                opens it recording. Typing happens in the drawer. */}
            <div className="w-full min-h-[50px] rounded-full bg-[#15354F] text-white flex items-center gap-2.5 pl-4 pr-1 py-1">
              <button type="button" onClick={() => openAgent()} data-open-chat=""
                className="flex-1 min-w-0 min-h-[42px] flex items-center gap-2.5 text-left text-[15px] font-bold active:opacity-90">
                <MessageCircle size={17} className="flex-shrink-0" />
                <span className="truncate">{t('agent.askAnythingPill', 'Ask me anything')}</span>
              </button>
              <button type="button" onClick={() => openAgent(null, 'voice')} data-open-chat-voice=""
                aria-label={t('agent.pillHint', 'Type or tap the mic to talk.')}
                className="flex-shrink-0 w-[42px] h-[42px] rounded-full bg-[#46A8F9] flex items-center justify-center active:scale-90 transition-transform">
                <Mic size={17} className="text-white" />
              </button>
            </div>
            <p className="text-[12px] text-gray-500 text-center mt-2">{t('agent.pillHint', 'Type or tap the mic to talk.')}</p>
          </div>
        ) : openOnly ? (
          <>
          {/* s124 (canvas "Orella Case Home — Revised" board A): Case Home puts the step's own action between Orella's
              message and Open chat, and then Open chat steps back to an outline so the screen has one navy button. */}
          {beforeOpen}
          <button
            type="button"
            onClick={() => openAgent()}
            data-open-chat=""
            className={`${beforeOpen ? 'mt-2' : 'mt-3'} w-full min-h-[46px] rounded-full text-[14px] font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform ${openTone === 'outline' ? 'bg-white text-[#15354F] border-[1.5px] border-[#D8E6F0]' : 'bg-[#15354F] text-white'}`}
          >
            <MessageCircle size={16} />
            {messages.length > 1
              ? t(messages.length === 1 ? 'agent.openChatCount_one' : 'agent.openChatCount_other', { defaultValue: 'Open chat · {{count}} messages', count: messages.length })
              : t('agent.openChat', 'Open chat')}
          </button>
          </>
        ) : (
        <div className="mt-3">
          {/* v242.71.0 — `composer="pill"` is Case Home's shape: a 999px capsule with
              two 33px round buttons, per mockup v5. Home keeps the 14px rounded
              box it has always had. Shape only — every handler is shared, so the
              two surfaces cannot drift in behaviour, only in silhouette. */}
          <div className={`flex items-end gap-2 border-[1.5px] border-[#A7D9FC] bg-white focus-within:border-[#46A8F9] transition-colors ${
            /* v242.78.0 (TF-31) — a FIXED radius, not rounded-full.
               `rounded-full` is 9999px, which on a one-line box is a pill and on
               a five-line box is a giant ellipse: the sides bow out and the text
               gets squeezed. 25px is half the collapsed height, so at rest it is
               pixel-identical to the pill, and as the textarea grows it becomes
               a rounded rectangle instead of an oval. */
            composer === 'pill' ? 'rounded-[25px] px-3.5 py-1' : 'rounded-[14px] px-3 py-1.5'}`}>
            {/* ★★★ v242.101.0 (TF b38) — "Left justify. Match font size to case
                home which has to be reduced so it fits in one line."
                THIS PARTLY REVERSES v242.99.0 BELOW, WHICH WAS ALSO DR. RAMI'S CALL
                ("Center and enlarge"), so both notes are kept: the sizing argument
                that follows is still the reason the TYPED text is 16px.
                ★★ THE MECHANISM IS THE SAME LEVER, PULLED THE OTHER WAY. index.css
                   forces 16px on the element and CANNOT reach ::placeholder, so the
                   placeholder is the only thing that can be sized independently —
                   which is why it renders at 16px today and wraps.
                ★ MEASURED, not guessed: Inter at 16px renders "Ask me anything about
                   your case…" at 266pt into a ~249pt column on a 393pt phone — it
                   cannot fit. 13px is 216pt and still fits the 231pt column of a
                   375pt phone, so it holds on the narrow devices too.
                ★★ AND `placeholder:whitespace-nowrap` MAKES IT STRUCTURAL: the
                   placeholder can never wrap again at any width or in any language;
                   a longer translation ellipsises instead of growing the pill.
                   The typed text still wraps normally — nowrap is scoped to the
                   pseudo-element, not the field.
                ★ Centring is gone entirely. It was `liveValue ? 'left' : 'center'`,
                   so the prompt sat centred and jumped left on the first keystroke.

                --- v242.99.0, retained for the 16px rationale ---
                THE PLACEHOLDER WAS PINNED SMALLER THAN THE TEXT IT STANDS IN FOR,
                and not by 2px. index.css:36 forces `input, select, textarea {
                font-size: 16px !important }` on mobile — the standard guard against
                iOS auto-zooming on focus — so the TYPED text renders at 16px however
                the `text-[14px]` below is written. But `placeholder:text-[12px]`
                targeted the ::placeholder pseudo-element, which that rule does not
                reach, so the placeholder alone stayed at 12. A 12px prompt sitting in
                a 16px field, left-aligned in a pill wide enough for a sentence: it
                read as a caption rather than an invitation to type. Dropping the
                override lets it inherit the field's own size, which is the fix.
                ★★ CENTRED ONLY WHILE EMPTY. text-align on a textarea styles the
                   VALUE too, so centring it outright would centre what the patient
                   types — every new line jumping to the middle as they write, which
                   is unreadable in a composer that grows to AGENT_INPUT_MAX_H. Keyed
                   to the value: the prompt is centred, and the moment a character
                   lands the text goes left where it belongs.
                ★ This comment sits in CHILDREN position, not between the attributes
                  where I first wrote it. A bare block comment between JSX attributes
                  does parse, but ORELLA_RULES §0(3) keeps comments out of the tag
                  because that habit has cost two production leaks.
                ★★ AND MOVING IT HERE BROKE THE FILE ON THE FIRST TRY: this very
                   paragraph spelled the delimiters out, and the closer inside the
                   prose ended the comment early. §1bd(a) — a comment body may not
                   contain a comment opener or closer, INCLUDING when it is only
                   describing one. check-parse caught it. */}
            <textarea
              ref={inputRef} value={liveValue} onChange={handleInputChange} onKeyDown={handleKeyDown}
              placeholder={openMd || pendingReply ? composerPlaceholder : (placeholder || t('home.searchPlaceholder'))} rows={1}
              className="flex-1 min-w-0 text-[14px] text-[#15354F] bg-transparent outline-none resize-none placeholder:text-gray-400 placeholder:text-[13px] placeholder:whitespace-nowrap placeholder:overflow-hidden placeholder:text-ellipsis leading-relaxed py-1.5"
              style={{ maxHeight: `${AGENT_INPUT_MAX_H}px`, textAlign: 'left' }}
            />
            <button
              onClick={handleVoiceToggle}
              disabled={agentTranscribing || loading}
              aria-label={agentListening ? 'Stop recording' : 'Start voice input'}
              className={`flex-shrink-0 flex items-center justify-center transition-all ${
                composer === 'pill' ? 'w-[33px] h-[33px] rounded-full' : 'w-[34px] h-[34px] rounded-[10px]'
              } ${
                agentListening ? 'bg-red-500 animate-pulse' : agentTranscribing ? 'bg-[#46A8F9] opacity-60' : 'bg-[#46A8F9] active:scale-90'
              }`}
            >
              {agentTranscribing ? (
                <OrellaSpinner size={16} tone="inherit" className="text-white" />
              ) : agentListening ? (
                <MicOff size={16} className="text-white" />
              ) : (
                <Mic size={16} className="text-white" />
              )}
            </button>
            <button onClick={handleSend} disabled={!input.trim() || loading}
              aria-label="Send message"
              className={`flex-shrink-0 flex items-center justify-center transition-all ${
                composer === 'pill' ? 'w-[33px] h-[33px] rounded-full' : 'w-[34px] h-[34px] rounded-[10px]'
              } ${
                input.trim() && !loading
                  ? 'bg-[#15354F] text-white active:scale-90'
                  /* Pill: idle send is neutral grey, per mockup v5. A navy-at-40%
                     button reads as a dimmed navy button — i.e. as something that
                     is nearly ready. Neutral grey reads as "nothing to send yet",
                     which is what is actually true. */
                  : (composer === 'pill' ? 'bg-[#E4EAF0] text-[#98A6B4]' : 'bg-[#15354F]/40 text-white')
              }`}
            >
              <Send size={15} className={input.trim() && !loading ? '' : 'opacity-50'} />
            </button>
          </div>
          {agentListening && (
            <div className="flex items-center gap-2 mt-1.5 px-1 text-[11px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              <span className="text-orella-700 truncate">
                {/* v242.66.0: the transcript itself now lives in the box above,
                    wrapped. This line was rendering the same partial a second time
                    inside a `truncate`, which is where the one-clipped-line look
                    came from. It is a STATUS indicator now, nothing more. */}
                {agentVoiceMode === 'speech' ? t('agent.listening') : t('agent.recording')}
              </span>
            </div>
          )}
          {agentVoiceError && (
            <div className="flex items-center gap-1.5 mt-1.5 px-1">
              <p className="text-[11px] text-red-500 font-medium flex-1">{agentVoiceError}</p>
              <button onClick={agentClearVoiceError} className="text-gray-400"><X size={10} /></button>
            </div>
          )}
        </div>
        )}
      </div>
    )
  }

  return createPortal(
    <>
      <style>{`
        @keyframes agentDotPulse { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.1); } }
        @keyframes agentFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes agentSheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes agentOverlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes agentFullscreenIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes agentHeaderIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes agentInputIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Backdrop — only for bottom-sheet mode */}
      {!fullscreen && (
        <div className="fixed inset-0 z-[9998] bg-black/40 animate-[agentOverlayIn_0.2s_ease]" onClick={onClose} />
      )}
      {/* Fullscreen mode: ICE_BLUE flash overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-[9997] bg-[#E9F8FF] animate-[agentOverlayIn_0.15s_ease]" style={{ opacity: 0, animationFillMode: 'none' }} />
      )}

      <div className={fullscreen
        ? "fixed inset-0 z-[9999] flex flex-col bg-white animate-[agentFullscreenIn_0.4s_cubic-bezier(0.4,0,0.2,1)]"
        : "fixed inset-x-0 bottom-0 z-[9999] flex justify-center"
      } data-bottom-inset="transparent positioning wrapper; the input row at the bottom of its white panel pays the inset (.bottom-actions)">
      <div className={fullscreen
        ? "w-full h-full flex flex-col bg-white"
        : "relative w-full max-w-3xl flex flex-col bg-white rounded-t-2xl shadow-2xl animate-[agentSheetUp_0.3s_ease] h-[88dvh] max-h-[88dvh]"
      }>
        {/* Header — fullscreen vs bottom-sheet */}
        {fullscreen ? (
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[#A7D9FC]/30 flex-shrink-0" style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 12px))', animation: 'agentHeaderIn 0.4s ease 0.1s both' }}>
            <button onClick={onClose} className="p-1 -ml-1 rounded-full hover:bg-gray-100 transition-colors">
              <ChevronLeft size={22} className="text-[#15354F]" />
            </button>
            <img src={orellaOIcon} alt="Orella" className="w-8 h-8 rounded-lg object-contain" />
            <div className="flex-1">
              <p className="text-[14px] font-extrabold text-[#15354F]">{t('agent.askOrella')}</p>
              <p className="text-[10.5px] font-semibold text-[#66D170] flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#66D170] inline-block" />
                {t('home.activeNow')}
              </p>
            </div>
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E9F8FF] border border-[#A7D9FC]/40">
              <Shield size={10} className="text-[#46A8F9]" />
              <span className="text-[9.5px] font-bold text-[#15354F]">HIPAA</span>
            </div>
          </div>
        ) : (
          <div className="flex-shrink-0 border-b border-gray-100" onTouchStart={handleSheetDragStart} onTouchEnd={handleSheetDragEnd} data-sheet-handle="">
          <div className="flex justify-center pt-2" aria-hidden="true">
            <span className="w-10 h-[5px] rounded-full bg-slate-300" />
          </div>
          <div className="flex items-center justify-between px-4 pt-1.5 pb-2.5">
            <div className="flex items-center gap-2.5">
              <img src={orellaOIcon} alt="Orella" className="w-8 h-8 rounded-lg object-contain" />
              <div>
                <p className="text-[14px] font-bold text-[#15354F]">{t('agent.askOrella')}</p>
                <p className="text-[11px] text-gray-400 font-medium">{t('agent.yourHealthCompanion')}</p>
              </div>
            </div>
            <button onClick={onClose} aria-label={t('agent.closeChat', 'Close chat')} className="w-11 h-11 -mr-2 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
              <ChevronDown size={22} className="text-gray-500" />
            </button>
          </div>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} onScroll={handleThreadScroll} onTouchStart={handleThreadTouchStart} onTouchMove={handleThreadTouchMove}
          className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 min-h-0" data-thread-scroll="">
          {/* s120: the messages sit at the BOTTOM of the sheet and grow upward (his note: "have the chat open from the
              bottom"). */}
          <div className="min-h-full flex flex-col justify-end space-y-3">
          {messages.map((msg, i) => {
            const msgTs = msg.timestamp
            const isLastMsg = i === messages.length - 1
            const actionsForMsg = pendingActions.filter(a => {
              const aTs = parseInt(a.id?.split('-')[0]) || 0
              return Math.abs(aTs - msgTs) < 500
            })

            return (
              <div key={`${msg.timestamp}-${i}`}>
                <AgentMessage msg={msg} isLast={isLastMsg} onStreamComplete={handleStreamComplete} />
                {/* Render profile action cards — wait for streaming to finish on latest message */}
                {msg.role === 'assistant' && !(isLastMsg && isStreaming) && actionsForMsg.map(action => (
                  <ProfileActionCard
                    key={action.id}
                    action={action}
                    onAccept={handleAcceptAction}
                    onDismiss={handleDismissAction}
                  />
                ))}
                {/* Render navigation card — wait for streaming to finish */}
                {msg.role === 'assistant' && msg.navigation && !(isLastMsg && isStreaming) && (
                  <NavigationCard navigation={msg.navigation} onNavigate={handleNavigate} />
                )}
                {/* v220.1 — Render messenger prep card (escalation to MD) */}
                {msg.role === 'assistant' && msg.messengerPrep && !msg._prepSent && !(isLastMsg && isStreaming) && (
                  <MessengerPrepCard
                    prep={msg.messengerPrep}
                    caseId={caseId}
                    msgRef={prepMsgRef(msg, i)}
                    doctorName={caseContext?.doctorName || 'your physician'}
                  />
                )}
              </div>
            )
          })}

          {failedSend && !loading && (
            <FailedSendNotice
              kind={failedSend.kind}
              language={language}
              t={t}
              retryLabel={t('agent.retry', 'Retry')}
              onRetry={() => retrySend(caseId)}
            />
          )}

          {loading && (
            <div className="flex items-end gap-2 animate-[agentFadeUp_0.3s_ease]">
              <img src={orellaOIcon} alt="" className="w-7 h-7 rounded-lg flex-shrink-0 object-contain" />
              <div className={`${DIALOGUE_BUBBLE} ${DIALOGUE_PAD}`}>
                <TypingDots />
              </div>
            </div>
          )}

          {!loading && !isStreaming && suggestions.length > 0 && (
            <div className="ml-9 animate-[agentFadeUp_0.3s_ease]">
              <QuickSuggestions suggestions={suggestions} kind={suggestionsKind} onSelect={handleSuggestionSelect} disabled={loading} />
            </div>
          )}
          </div>
        </div>

        {!fullscreen && unseen > 0 && (
          <button
            type="button"
            onClick={jumpToLatest}
            data-jump-latest=""
            className="absolute right-4 bottom-[96px] z-10 min-h-[40px] px-3.5 rounded-full bg-[#15354F] text-white text-[12.5px] font-bold flex items-center gap-1.5 shadow-lg animate-[agentFadeUp_0.2s_ease]"
          >
            {t(unseen === 1 ? 'agent.newMessages_one' : 'agent.newMessages_other', { defaultValue: '{{count}} new messages', count: unseen })}
            <ArrowDown size={14} />
          </button>
        )}

        {/* Input */}
        <div className="flex-shrink-0 border-t border-gray-100 px-3 py-2.5 bg-white bottom-actions [--ba-min:10px]" style={fullscreen ? { animation: 'agentInputIn 0.4s ease 0.15s both' } : undefined}>
          {inputFocused && !fullscreen && (
            <p className="text-[11px] text-gray-500 text-center mb-1.5" data-keyboard-hint="">
              {t('agent.keyboardHint', 'Swipe down on the messages to put the keyboard away')}
            </p>
          )}
          <div className="flex items-end gap-2">
            {inputFocused && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={hideKeyboard}
                aria-label={t('agent.hideKeyboard', 'Hide keyboard')}
                data-hide-keyboard=""
                className="flex-shrink-0 w-10 h-11 flex items-center justify-center rounded-full text-gray-500 active:bg-gray-100"
              >
                <Keyboard size={20} />
              </button>
            )}
            <div className="flex-1 bg-gray-50 rounded-2xl border border-gray-200 px-3.5 py-2.5 focus-within:border-[#46A8F9] focus-within:bg-white transition-colors">
              <textarea
                ref={inputRef} value={liveValue} onChange={handleInputChange} onKeyDown={handleKeyDown}
                onFocus={() => setInputFocused(true)} onBlur={() => setInputFocused(false)}
                placeholder={composerPlaceholder} rows={1}
                className="w-full text-[13.5px] text-[#15354F] bg-transparent outline-none resize-none placeholder:text-gray-400 leading-relaxed"
                style={{ maxHeight: `${AGENT_INPUT_MAX_H}px` }}
              />
            </div>
            {/* v169.3.0: Live voice input button */}
            <button 
              onClick={handleVoiceToggle}
              disabled={agentTranscribing || loading}
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                agentListening 
                  ? 'bg-red-500 animate-pulse' 
                  : agentTranscribing 
                    ? 'bg-gray-100' 
                    : 'bg-gray-50 hover:bg-gray-100 active:scale-95'
              }`}
            >
              {agentTranscribing ? (
                <OrellaSpinner size={16} />
              ) : agentListening ? (
                <MicOff size={16} className="text-white" />
              ) : (
                <Mic size={16} className="text-gray-400" />
              )}
            </button>
            <button onClick={handleSend} disabled={!input.trim() || loading}
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                input.trim() && !loading ? 'bg-[#15354F] text-white active:scale-95' : 'bg-gray-100 text-gray-300'
              }`}
            >
              <Send size={17} className={input.trim() && !loading ? '' : 'opacity-50'} />
            </button>
          </div>
          {/* Voice listening status — live interim text */}
          {agentListening && (
            <div className="flex items-center gap-2 mt-1.5 px-1 text-[11px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
              <span className="text-orella-700 truncate">
                {/* v242.66.0: the transcript itself now lives in the box above,
                    wrapped. This line was rendering the same partial a second time
                    inside a `truncate`, which is where the one-clipped-line look
                    came from. It is a STATUS indicator now, nothing more. */}
                {agentVoiceMode === 'speech' ? t('agent.listening') : t('agent.recording')}
              </span>
            </div>
          )}
          {agentVoiceError && (
            <div className="flex items-center gap-1.5 mt-1.5 px-1">
              <p className="text-[11px] text-red-500 font-medium flex-1">{agentVoiceError}</p>
              <button onClick={agentClearVoiceError} className="text-gray-400"><X size={10} /></button>
            </div>
          )}
        </div>
      </div>
      </div>
    </>,
    document.body
  )
}


/** AgentEntryChip — small button shown below static Orella chat bubbles */
export function AgentEntryChip({ onClick }) {
  const { t } = useI18n()
  return (
    <button onClick={onClick}
      className="flex items-center gap-1.5 mt-2 ml-[52px] px-3 py-1.5 rounded-full bg-white border border-[#A7D9FC] hover:bg-[#E9F8FF] active:bg-[#D4EFFF] transition-all group"
    >
      <MessageCircle size={13} className="text-[#46A8F9] group-hover:text-[#15354F] transition-colors" />
      <span className="text-[11.5px] font-semibold text-[#46A8F9] group-hover:text-[#15354F] transition-colors">
        {t('agent.haveAQuestionAskOrella')}
      </span>
    </button>
  )
}

/** HomeAgentBubble — v143.4: 90px FAB with Orella-style ice blue speech bubble.
 *  HomeScreen only — larger presence to fill the empty space below action cards.
 *  Animation sequence: FAB fades in → bubble slides up → dots pulse → headline + subtitle.
 *  Auto-dismisses bubble after 8s, leaves FAB. Plays once per session.
 */
export function HomeAgentBubble({ onClick }) {
  const { t } = useI18n()
  const [showFab, setShowFab] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    const key = 'orella_home_agent_bubble'
    const hasPlayed = sessionStorage.getItem(key)
    
    if (hasPlayed) {
      setShowFab(true)
      setDismissed(true)
      return
    }
    
    const timers = []
    timers.push(setTimeout(() => setShowFab(true), 600))
    timers.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, 1000))
    timers.push(setTimeout(() => { setShowDots(false); setShowText(true) }, 1800))
    timers.push(setTimeout(() => { 
      setDismissed(true)
      sessionStorage.setItem(key, 'true')
    }, 8000))
    
    return () => timers.forEach(clearTimeout)
  }, [])
  
  return (
    <>
      <style>{`
        @keyframes agentDotPulse { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.1); } }
        @keyframes agentFabPulse { 0%, 100% { box-shadow: 0 8px 32px rgba(21,53,79,0.45); } 50% { box-shadow: 0 8px 40px rgba(70,168,249,0.4); } }
      `}</style>
      <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-3xl z-[9990] pointer-events-none">
        <div className="absolute bottom-0 right-4 flex items-end gap-2.5">
          {/* Speech bubble — ice blue Orella dialogue style */}
          {showBubble && !dismissed && (
            <div 
              className="px-4 py-3.5 max-w-[240px] pointer-events-auto cursor-pointer"
              style={{ 
                backgroundColor: '#E9F8FF',
                border: '1px solid #A7D9FC',
                borderRadius: '18px 18px 4px 18px',
                boxShadow: '0 4px 16px rgba(21,53,79,0.1)',
                opacity: showBubble && !dismissed ? 1 : 0,
                transform: showBubble && !dismissed ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.5s ease, transform 0.5s ease',
              }}
              onClick={onClick}
            >
              {showDots && !showText && (
                <div className="flex gap-1.5 py-1">
                  {[0, 0.15, 0.3].map((delay, i) => (
                    <span key={i} className="w-1.5 h-1.5 rounded-full" 
                      style={{ backgroundColor: '#A7D9FC', animation: `agentDotPulse 1.2s ease-in-out infinite ${delay}s` }} 
                    />
                  ))}
                </div>
              )}
              {showText && (
                <div className="animate-[fadeIn_0.3s_ease]">
                  <p style={{ color: '#15354F', fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>{t('agent.haveAQuestionICan')}</p>
                </div>
              )}
            </div>
          )}
          
          {/* 90px FAB — scaled up from 56px, same design */}
          <button
            onClick={onClick}
            className="rounded-full flex items-center justify-center active:scale-90 transition-all pointer-events-auto relative flex-shrink-0"
            style={{ 
              width: 80, height: 80,
              backgroundColor: '#15354F', 
              animation: showFab ? 'agentFabPulse 3s ease-in-out infinite' : 'none',
              opacity: showFab ? 1 : 0,
              transform: showFab ? 'scale(1)' : 'scale(0.75)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
            }}
            aria-label={t('agent.chatWithOrella')}
          >
            <img src={orellaOWhite} alt="" className="object-contain" style={{ width: 40, height: 40 }} />
            <div 
              className="absolute -top-1 -right-1 rounded-full flex items-center justify-center border-[2.5px] border-white" 
              style={{ backgroundColor: '#46A8F9', width: 26, height: 26 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </>
  )
}

/** AgentFAB — v153.1: Tab-first design, right-edge docked.
 *  
 *  variant='home': 44×64px tab with large O + chat bubble (Home screen)
 *  variant='tab': 32×64px tab with chat bubble + O (all other screens)
 *  Both auto-shrink to 14×64px mini after 5s idle or swipe-right dismiss.
 *  Tap any state → opens agent drawer.
 */
export function AgentFAB({ onClick, visible = true, variant = 'tab', dialogueMessage = null }) {
  const { t } = useI18n()
  // v177.0.0: Track whether user has dismissed to mini this session
  const dismissedToMiniRef = useRef(() => {
    try { return sessionStorage.getItem('orella_agent_fab_dismissed') === 'true' } catch { return false }
  })
  const wasDismissed = dismissedToMiniRef.current()
  // States: 'home' | 'tab' | 'mini' — start mini if already dismissed this session
  const [mode, setMode] = useState(() => {
    if (variant === 'home') return 'home'
    return wasDismissed ? 'mini' : 'tab'
  })
  const [showSpeech, setShowSpeech] = useState(false)
  const [speechFading, setSpeechFading] = useState(false)
  const idleRef = useRef(null)
  const fabRef = useRef(null)
  const dragRef = useRef({ startX: 0, startY: 0, startTime: 0, currentX: 0, dragging: false, decided: false })

  // Reset mode when variant changes — but respect session dismissal
  useEffect(() => {
    if (variant === 'home') {
      setMode('home')
    } else {
      // Only reset to 'tab' if user hasn't dismissed to mini this session
      try {
        if (sessionStorage.getItem('orella_agent_fab_dismissed') === 'true') {
          setMode('mini')
        } else {
          setMode('tab')
        }
      } catch {
        setMode('tab')
      }
    }
  }, [variant])

  // Auto-shrink: tab → mini after 5s idle (home stays as-is, dialogueMessage keeps full)
  useEffect(() => {
    if (!visible || mode !== 'tab' || dialogueMessage) return
    clearTimeout(idleRef.current)
    idleRef.current = setTimeout(() => {
      setMode('mini')
      try { sessionStorage.setItem('orella_agent_fab_dismissed', 'true') } catch { /* ignore */ }
    }, 5000)

    const resetIdle = () => {
      if (mode !== 'tab') return
      clearTimeout(idleRef.current)
      idleRef.current = setTimeout(() => {
        setMode('mini')
        try { sessionStorage.setItem('orella_agent_fab_dismissed', 'true') } catch { /* ignore */ }
      }, 5000)
    }
    window.addEventListener('scroll', resetIdle, true)
    window.addEventListener('touchstart', resetIdle, true)
    return () => {
      clearTimeout(idleRef.current)
      window.removeEventListener('scroll', resetIdle, true)
      window.removeEventListener('touchstart', resetIdle, true)
    }
  }, [visible, mode])

  // Track if touch already handled the tap (prevents double-fire on touch devices)
  const touchHandledRef = useRef(false)

  // Swipe-to-dismiss touch handlers (for tab and home modes)
  useEffect(() => {
    const fab = fabRef.current
    if (!fab || !visible || mode === 'mini') return

    const onTouchStart = (e) => {
      const touch = e.touches[0]
      dragRef.current = { startX: touch.clientX, startY: touch.clientY, startTime: Date.now(), currentX: 0, dragging: false, decided: false }
      fab.style.transition = 'none'
      touchHandledRef.current = false
    }

    const onTouchMove = (e) => {
      const touch = e.touches[0]
      const d = dragRef.current
      const dx = touch.clientX - d.startX
      const dy = Math.abs(touch.clientY - d.startY)

      if (d.decided && !d.dragging) return

      if (!d.decided && (Math.abs(dx) > 6 || dy > 6)) {
        d.decided = true
        d.dragging = (dx > 0 && Math.abs(dx) > dy)
        if (!d.dragging) return
      }

      if (d.dragging) {
        d.currentX = Math.max(0, dx)
        fab.style.transform = `translateX(${d.currentX}px)`
        fab.style.opacity = `${Math.max(0.3, 1 - d.currentX / 100)}`
        e.preventDefault()
      }
    }

    const onTouchEnd = () => {
      const d = dragRef.current
      const elapsed = Date.now() - d.startTime
      const velocity = d.currentX / Math.max(elapsed, 1)

      fab.style.transition = 'transform 0.2s ease, opacity 0.2s ease'

      if (d.dragging && (d.currentX > 40 || velocity > 0.3)) {
        // Dismiss → collapse to mini tab
        fab.style.transform = 'translateX(100px)'
        fab.style.opacity = '0'
        setTimeout(() => {
          setMode('mini')
          try { sessionStorage.setItem('orella_agent_fab_dismissed', 'true') } catch { /* ignore */ }
          if (fab) {
            fab.style.transform = ''
            fab.style.opacity = ''
          }
        }, 200)
      } else if (d.dragging) {
        fab.style.transform = 'translateX(0px)'
        fab.style.opacity = '1'
      } else if (!d.decided || d.currentX < 5) {
        fab.style.transform = 'translateX(0px)'
        fab.style.opacity = '1'
        if (elapsed < 400) {
          touchHandledRef.current = true
          onClick()
        }
      }

      d.dragging = false
      d.decided = false
      d.currentX = 0
    }

    fab.addEventListener('touchstart', onTouchStart, { passive: true })
    fab.addEventListener('touchmove', onTouchMove, { passive: false })
    fab.addEventListener('touchend', onTouchEnd, { passive: true })
    fab.addEventListener('touchcancel', onTouchEnd, { passive: true })

    return () => {
      fab.removeEventListener('touchstart', onTouchStart)
      fab.removeEventListener('touchmove', onTouchMove)
      fab.removeEventListener('touchend', onTouchEnd)
      fab.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [visible, mode, onClick])

  // Home speech bubble — appears 1.2s after mount, fades after 5s (once per session)
  useEffect(() => {
    if (mode !== 'home') return
    const key = 'orella_home_speech_shown'
    if (sessionStorage.getItem(key)) return
    
    const t1 = setTimeout(() => setShowSpeech(true), 1200)
    const t2 = setTimeout(() => {
      setSpeechFading(true)
      sessionStorage.setItem(key, 'true')
    }, 6200)
    const t3 = setTimeout(() => { setShowSpeech(false); setSpeechFading(false) }, 6800)
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3) }
  }, [mode])

  // Dialogue message bubble for tab mode — show after 0.8s, persist (no auto-dismiss)
  // When dialogueMessage is set, force mode to 'tab' (even if already mini)
  const [showDialogue, setShowDialogue] = useState(false)
  useEffect(() => {
    if (dialogueMessage) {
      // Force back to full tab mode when dialogue is active
      if (mode === 'mini') setMode('tab')
      const t = setTimeout(() => setShowDialogue(true), 800)
      return () => clearTimeout(t)
    } else {
      setShowDialogue(false)
    }
  }, [dialogueMessage, mode])

  if (!visible) return null

  // Shimmer keyframes for tab glow
  const shimmerStyle = `
    @keyframes agentTabShimmer {
      0%, 100% { box-shadow: -3px 0 12px rgba(21,53,79,0.25); }
      50% { box-shadow: -3px 0 20px rgba(70,168,249,0.45), -1px 0 8px rgba(70,168,249,0.25); }
    }
    @keyframes fabFadeUp {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `

  // Blue circle with white chat icon — overflows the navy tab like a badge
  const ChatBadge = ({ size, circleSize }) => (
    <div
      className="flex items-center justify-center"
      style={{
        width: `${circleSize}px`,
        height: `${circleSize}px`,
        borderRadius: '50%',
        backgroundColor: '#46A8F9',
        boxShadow: '0 2px 6px rgba(70,168,249,0.4)',
      }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'scaleX(-1)' }}>
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    </div>
  )

  // ─── Mini (14×64px) — navy sliver with blue badge overflowing left ───
  if (mode === 'mini') {
    return (
      <div className="fixed bottom-[104px] left-1/2 -translate-x-1/2 w-full max-w-3xl z-[9990] pointer-events-none">
        <button
          onClick={() => {
            setMode('tab')
            onClick()
          }}
          className="absolute bottom-0 right-0 pointer-events-auto"
          style={{
            width: '14px',
            height: '64px',
            backgroundColor: '#15354F',
            borderRadius: '10px 0 0 10px',
            boxShadow: '-2px 0 8px rgba(21,53,79,0.15)',
            overflow: 'visible',
          }}
          aria-label={t('agent.openOrellaChat')}
        >
          <div className="absolute" style={{ top: '50%', left: '-5px', transform: 'translateY(-50%)' }}>
            <ChatBadge size={8} circleSize={14} />
          </div>
        </button>
      </div>
    )
  }

  // ─── Tab (32×64px) — O logo centered, blue chat badge overflows top-left ───
  if (mode === 'tab') {
    return (
      <div className="fixed bottom-[104px] left-1/2 -translate-x-1/2 w-full max-w-3xl z-[9990] pointer-events-none">
        <style>{shimmerStyle}</style>
        {/* Dialogue bubble — ice blue Orella style, shown when dialogueMessage is set */}
        {showDialogue && dialogueMessage && (
          <div 
            className="absolute bottom-1 right-[50px] pointer-events-auto"
            style={{
              maxWidth: '220px',
              opacity: 1,
              animation: 'fabFadeUp 0.35s ease',
            }}
            onClick={() => { onClick(); setShowDialogue(false) }}
          >
            <div 
              style={{
                backgroundColor: '#E9F8FF',
                border: '1px solid #A7D9FC',
                borderRadius: '14px 14px 4px 14px',
                padding: '10px 14px',
                boxShadow: '0 4px 16px rgba(21,53,79,0.08)',
              }}
            >
              <p style={{ fontSize: '13px', fontWeight: 500, color: '#15354F', margin: 0, lineHeight: 1.5 }}>
                {dialogueMessage}
              </p>
            </div>
          </div>
        )}
        <button
          ref={fabRef}
          onClick={() => { if (!touchHandledRef.current) onClick(); touchHandledRef.current = false }}
          className="absolute bottom-0 right-0 pointer-events-auto flex items-center justify-center"
          style={{
            width: dialogueMessage ? '44px' : '32px',
            height: '64px',
            backgroundColor: '#15354F',
            borderRadius: '14px 0 0 14px',
            animation: 'agentTabShimmer 3s ease-in-out infinite',
            touchAction: 'none',
            overflow: 'visible',
          }}
          aria-label={t('agent.chatWithOrella')}
        >
          <img src={orellaOWhite} alt="" style={{ width: dialogueMessage ? '30px' : '20px', height: dialogueMessage ? '30px' : '20px', objectFit: 'contain' }} />
          <div className="absolute" style={{ top: '-6px', left: '-8px' }}>
            <ChatBadge size={dialogueMessage ? 14 : 10} circleSize={dialogueMessage ? 28 : 20} />
          </div>
        </button>
      </div>
    )
  }

  // ─── Home (44×64px) — large O logo + speech bubble dialogue to the left ───
  return (
    <div className="fixed bottom-[104px] left-1/2 -translate-x-1/2 w-full max-w-3xl z-[9990] pointer-events-none">
      <style>{shimmerStyle}</style>
      {/* Speech bubble — appears to the left of the tab */}
      {showSpeech && (
        <div 
          className="absolute bottom-1 right-[62px] pointer-events-auto"
          style={{
            maxWidth: '220px',
            opacity: speechFading ? 0 : 1,
            transform: speechFading ? 'translateX(8px)' : 'translateX(0)',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
          }}
          onClick={() => { onClick(); setShowSpeech(false) }}
        >
          <div 
            style={{
              backgroundColor: '#E9F8FF',
              border: '1px solid #A7D9FC',
              borderRadius: '14px 14px 4px 14px',
              padding: '10px 14px',
              boxShadow: '0 4px 16px rgba(21,53,79,0.08)',
            }}
          >
            <p style={{ fontSize: '13px', fontWeight: 500, color: '#15354F', margin: 0, lineHeight: 1.5 }}>
              {t('agent.haveAQuestionICan')}
            </p>
          </div>
        </div>
      )}
      
      <button
        ref={fabRef}
        onClick={() => { if (!touchHandledRef.current) onClick(); touchHandledRef.current = false }}
        className="absolute bottom-0 right-0 pointer-events-auto flex items-center justify-center"
        style={{
          width: '44px',
          height: '64px',
          backgroundColor: '#15354F',
          borderRadius: '14px 0 0 14px',
          animation: 'agentTabShimmer 3s ease-in-out infinite',
          touchAction: 'none',
          overflow: 'visible',
        }}
        aria-label={t('agent.chatWithOrella')}
      >
        <img src={orellaOWhite} alt="" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
        <div className="absolute" style={{ top: '-8px', left: '-10px' }}>
          <ChatBadge size={14} circleSize={28} />
        </div>
      </button>
    </div>
  )
}
